import { computed, inject, Injectable, signal } from '@angular/core';
import {
  createUserWithEmailAndPassword,
  deleteUser,
  GithubAuthProvider,
  GoogleAuthProvider,
  sendPasswordResetEmail,
  signInWithEmailAndPassword,
  signInWithPopup,
  signOut,
  UserCredential,
} from 'firebase/auth';
import { doc, setDoc } from 'firebase/firestore';
import { toSignal } from '@angular/core/rxjs-interop';
import { map } from 'rxjs/operators';
import { from, Observable } from 'rxjs';

import { FIREBASE_AUTH, FIRESTORE } from '../firebase/firebase.tokens';
import { authState$ } from '../firebase/rx-firebase';
import { User } from '../../shared/models/user';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private readonly auth = inject(FIREBASE_AUTH);
  private readonly firestore = inject(FIRESTORE);

  /** Firebase auth state as a signal */
  private readonly userState$ = authState$(this.auth);
  private readonly firebaseUser = toSignal(this.userState$, { initialValue: undefined });

  /** Current Firebase user */
  readonly currentUser = this.firebaseUser;

  /** Whether user is logged in (null while loading) */
  readonly isLoggedIn = computed(() => {
    const user = this.firebaseUser();
    if (user === undefined) return null; // Still loading
    return user !== null;
  });

  /** Observable that emits true when user is authenticated, used by guards */
  readonly authReady$: Observable<boolean> = from(this.auth.authStateReady()).pipe(
    map(() => this.auth.currentUser !== null)
  );

  /** Loading state for auth operations */
  readonly loading = signal(false);

  /** Error message from auth operations */
  readonly error = signal<string | null>(null);

  /**
   * Refresh the user's ID token to keep the session alive.
   * This prevents Firebase from logging out users during long video sessions.
   */
  async refreshToken(): Promise<void> {
    const user = this.auth.currentUser;
    if (user) {
      try {
        await user.getIdToken(true); // Force refresh
      } catch (err) {
        console.error('Error refreshing token:', err);
      }
    }
  }

  /**
   * Sign in with email and password
   */
  async signInWithEmail(email: string, password: string): Promise<UserCredential> {
    this.loading.set(true);
    this.error.set(null);

    try {
      const credential = await signInWithEmailAndPassword(this.auth, email, password);
      return credential;
    } catch (err) {
      this.error.set(this.getErrorMessage(err));
      throw err;
    } finally {
      this.loading.set(false);
    }
  }

  /**
   * Create account with email and password
   */
  async createAccountWithEmail(email: string, password: string, name: string): Promise<UserCredential> {
    this.loading.set(true);
    this.error.set(null);

    try {
      const credential = await createUserWithEmailAndPassword(this.auth, email, password);
      await this.createUserDocument({
        id: credential.user.uid,
        name,
        email,
        created: new Date(),
      });
      return credential;
    } catch (err) {
      this.error.set(this.getErrorMessage(err));
      throw err;
    } finally {
      this.loading.set(false);
    }
  }

  /**
   * Sign in with Google
   */
  async signInWithGoogle(): Promise<UserCredential> {
    this.loading.set(true);
    this.error.set(null);

    try {
      const provider = new GoogleAuthProvider();
      provider.addScope('profile');
      provider.addScope('email');

      const credential = await signInWithPopup(this.auth, provider);

      // Create user document if new user
      if (credential.user) {
        const profile = (credential as UserCredential & { _tokenResponse?: { isNewUser?: boolean } })._tokenResponse;
        if (profile?.isNewUser) {
          await this.createUserDocument({
            id: credential.user.uid,
            name: credential.user.displayName ?? '',
            email: credential.user.email ?? '',
            created: new Date(),
          });
        }
      }

      return credential;
    } catch (err) {
      this.error.set(this.getErrorMessage(err));
      throw err;
    } finally {
      this.loading.set(false);
    }
  }

  /**
   * Sign in with GitHub
   */
  async signInWithGithub(): Promise<UserCredential> {
    this.loading.set(true);
    this.error.set(null);

    try {
      const provider = new GithubAuthProvider();
      const credential = await signInWithPopup(this.auth, provider);

      // Create user document if new user
      if (credential.user) {
        const profile = (credential as UserCredential & { _tokenResponse?: { isNewUser?: boolean } })._tokenResponse;
        if (profile?.isNewUser) {
          await this.createUserDocument({
            id: credential.user.uid,
            name: credential.user.displayName ?? '',
            email: credential.user.email ?? '',
            created: new Date(),
          });
        }
      }

      return credential;
    } catch (err) {
      this.error.set(this.getErrorMessage(err));
      throw err;
    } finally {
      this.loading.set(false);
    }
  }

  /**
   * Send password reset email
   */
  async sendPasswordReset(email: string): Promise<void> {
    this.loading.set(true);
    this.error.set(null);

    try {
      await sendPasswordResetEmail(this.auth, email);
    } catch (err) {
      this.error.set(this.getErrorMessage(err));
      throw err;
    } finally {
      this.loading.set(false);
    }
  }

  /**
   * Sign out
   */
  async logout(): Promise<void> {
    this.loading.set(true);
    this.error.set(null);

    try {
      await signOut(this.auth);
    } catch (err) {
      this.error.set(this.getErrorMessage(err));
      throw err;
    } finally {
      this.loading.set(false);
    }
  }

  /**
   * Delete user account permanently
   * This will delete the Firebase Auth account
   */
  async deleteAccount(): Promise<void> {
    this.loading.set(true);
    this.error.set(null);

    try {
      const user = this.auth.currentUser;
      if (!user) {
        throw new Error('Usuário não encontrado');
      }

      // Delete the Firebase Auth user
      await deleteUser(user);
    } catch (err) {
      this.error.set(this.getErrorMessage(err));
      throw err;
    } finally {
      this.loading.set(false);
    }
  }

  /**
   * Clear error state
   */
  clearError(): void {
    this.error.set(null);
  }

  /**
   * Create user document in Firestore
   */
  private async createUserDocument(user: User): Promise<void> {
    const userRef = doc(this.firestore, 'users', user.id);
    await setDoc(userRef, user);
  }

  /**
   * Get user-friendly error message
   */
  private getErrorMessage(error: unknown): string {
    const firebaseError = error as { code?: string; message?: string };

    const errorMessages: Record<string, string> = {
      'auth/user-not-found': 'Usuário não encontrado.',
      'auth/wrong-password': 'Senha incorreta.',
      'auth/email-already-in-use': 'Este email já está em uso.',
      'auth/weak-password': 'A senha deve ter pelo menos 6 caracteres.',
      'auth/invalid-email': 'Email inválido.',
      'auth/account-exists-with-different-credential': 'Conta já existe com outra forma de login.',
      'auth/popup-closed-by-user': 'Login cancelado.',
      'auth/network-request-failed': 'Erro de conexão. Verifique sua internet.',
      'auth/too-many-requests': 'Muitas tentativas. Tente novamente mais tarde.',
      'auth/invalid-credential': 'Credenciais inválidas.',
      'auth/requires-recent-login': 'Por segurança, faça login novamente antes de excluir sua conta.',
    };

    return errorMessages[firebaseError.code ?? ''] ?? firebaseError.message ?? 'Erro desconhecido.';
  }
}
